/* eslint-disable no-undef */
import React from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import VWDigitalSignage from './components/DigitalSignage/VWDigitalSignage';
import RemoteFilmstrip from './components/Filmstrip/RemoteFilmstrip';
import SelfLargeVideo from './components/LargeVideo/SelfLargeVideo';
import StageFilmstrip from './components/Filmstrip/StageFilmstrip';
import JoinAudioVideo from './components/Toolbox/JoinAudioVideo';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import ConferenceSetting from './components/ConferenceSetting';
import LargeVideo from './components/LargeVideo/LargeVideo';
import Whiteboard from './components/Whiteboard/Whiteboard';
import VideoLayout from 'util/UI/videolayout/VideoLayout';
import ConferenceInfo from './components/ConferenceInfo';
import ToolboxWeb from './components/Toolbox/ToolboxWeb';
import VWBrowser from './components/Browser/VWBrowser';
import Toolbox from './components/Toolbox/Toolbox';
import Message from './components/Message';
import Notice from './components/Notice';
import { getRemoteParticipantCount } from 'store/modules/base/participants/functions';
import { getConferenceNameForTitle } from 'store/modules/base/conference/functions';
import { connect as reactReduxConnect } from 'store/modules/base/redux/functions';
import { shouldDisplayNotifications } from 'store/modules/conference/functions';
import { getLargeVideoParticipant } from 'store/modules/large-video/functions';
import { fullScreenChanged, showToolbox } from 'store/modules/toolbox/actions';
import { shouldDisplayTileView } from 'store/modules/video-layout/functions';
import { isLocalTrackMuted } from 'store/modules/base/tracks/functions';
import { isMobileBrowser } from 'store/modules/base/environment/utils';
import { disconnect } from 'store/modules/base/connection/actions';
import { MEDIA_TYPE } from 'store/modules/base/media/constants';
import { translate } from 'store/modules/base/i18n/functions';
import { init } from 'store/modules/conference/actions';
import { GradientBackground } from 'assets/images';
import { withRouter } from 'modules/withRouter';
import { Box, Drawer, List } from '@mui/material';
import { Labels } from 'util/constants';
import { Home } from 'modules/LogIn';
import {
  LAYOUTS,
  LAYOUT_CLASSNAMES
} from 'store/modules/video-layout/constants';
import {
  getCollabDetails,
  isWhiteboardEnabled,
  isWhiteboardVisible
} from 'store/modules/whiteboard/functions';

import {
  toggleGroups,
  toggleSettings,
  updateSleepSettings
} from 'store/modules/settings/actions';
import {
  leaveConference,
  reloadWithStoredParams
} from 'store/modules/app/actions';

/**
 * DOM events for when full screen mode has changed. Different browsers need
 * different vendor prefixes.
 *
 * @private
 * @type {Array<string>}
 */
const FULL_SCREEN_EVENTS = [
  'webkitfullscreenchange',
  'mozfullscreenchange',
  'fullscreenchange'
];

/**
 * The conference page of the Web application.
 */
class Conference extends React.Component {
  state = {
    videoRotationModeEnabled: false
  };

  /**
   * Initializes a new Conference instance.
   *
   * @param {Object} props - The read-only properties with which the new
   * instance is to be initialized.
   */
  constructor(props) {
    super(props);

    const { _mouseMoveCallbackInterval } = props;

    this._timeoutRef = React.createRef();

    // Throttle and bind this component's mousemove handler to prevent it
    // from firing too often.
    this._originalOnShowToolbar = this._onShowToolbar;
    this._originalOnMouseMove = this._onMouseMove;
    this.state = { videoRotationModeEnabled: false };
    this._onShowToolbar = throttle(() => this._originalOnShowToolbar(), 100, {
      leading: true,
      trailing: false
    });

    this._onMouseMove = throttle(
      (event) => this._originalOnMouseMove(event),
      _mouseMoveCallbackInterval,
      {
        leading: true,
        trailing: false
      }
    );

    // Bind event handler so it is only bound once for every instance.
    this._onFullScreenChange = this._onFullScreenChange.bind(this);
    this._onVidespaceTouchStart = this._onVidespaceTouchStart.bind(this);
    this._setBackground = this._setBackground.bind(this);
    this.renderWhiteLayer = this.renderWhiteLayer.bind(this);
  }

  /**
   * Start the connection and get the UI ready for the conference.
   *
   * @inheritdoc
   */
  componentDidMount() {
    if (this.props._conferenceSetting) this._startTimer();
    if (this.props._roomName) {
      this._start();
    } else {
      this.props
        .dispatch(reloadWithStoredParams())
        .then(() => this._start(), 1000);
    }
  }

  /**
   * Calls into legacy UI to update the application layout, if necessary.
   *
   * @inheritdoc
   * returns {void}
   */
  componentDidUpdate(prevProps) {
    if (
      this.props._shouldDisplayTileView === prevProps._shouldDisplayTileView
    ) {
      return;
    }

    // TODO: For now VideoLayout is being called as LargeVideo and Filmstrip
    // sizing logic is still handled outside of React. Once all components
    // are in react they should calculate size on their own as much as
    // possible and pass down sizings.
    VideoLayout.refreshLayout();
  }

  /**
   * Disconnect from the conference when component will be
   * unmounted.
   *
   * @inheritdoc
   */
  componentWillUnmount() {
    this._clearTimer();
    APP.UI.unbindEvents();

    FULL_SCREEN_EVENTS.forEach((name) =>
      document.removeEventListener(name, this._onFullScreenChange)
    );
    APP.conference.isJoined() && this.props.dispatch(disconnect());
  }

  /**
   * Sets a 60 second timer to close the settings drawer.
   */
  _startTimer = () => {
    if (this._timeoutRef.current) clearTimeout(this._timeoutRef.current);
    this._timeoutRef.current = setTimeout(() => {
      this._clearTimer();
      this.toggleSettings(false);
    }, 60 * 1000);
  };

  /**
   * Clears the close timer
   */
  _clearTimer = () => {
    if (this._timeoutRef.current) clearTimeout(this._timeoutRef.current);
    this._timeoutRef.current = null;
  };

  /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
  render() {
    const {
      _layoutClassName,
      _isWhiteboardOpen,
      _whiteboardReady,
      _shouldDisplayTileView,
      _videoMuted,
      _isBrowser,
      _isBrowserOptions,
      _isDigitalSignage,
      _isDigitalSignageOptions,
      _conferenceSetting,
      _conferenceGroups,
      _isPortrait,
      _userIdleMode,
      _userDevice,
      _largeVideoParticipant,
      _participantLength,
      _sensoringDebugMode,
      _oddRotation,
      _activeParticipants,
      _clientHeight,
      _clientWidth
    } = this.props;
    let splits = [1];
    if (_isBrowser && _isBrowserOptions) {
      if (_isBrowserOptions.browserStyle === 'side-by-side') {
        splits = _isBrowserOptions.browserToScreenRatio.split(':');
      }
    } else if (_isDigitalSignage && _isDigitalSignageOptions) {
      if (_isDigitalSignageOptions.signageStyle === 'side-by-side') {
        splits = _isDigitalSignageOptions.signageToScreenRatio.split(':');
      }
    }
    return (
      <div
        className="App"
        style={{ flexDirection: _isPortrait ? 'column' : 'row' }}
        onClick={(event) => {
          if (!_isWhiteboardOpen) {
            event.preventDefault();
            this.startIdleTimer();
          }
        }}>
        <div
          id="layout_wrapper"
          style={{ flex: splits[0] }}
          onMouseEnter={this._onMouseEnter}
          onMouseLeave={this._onMouseLeave}
          onMouseMove={this._onMouseMove}
          ref={this._setBackground}>
          <div
            className={_layoutClassName}
            id="videoconference_page"
            onMouseMove={isMobileBrowser() ? undefined : this._onShowToolbar}>
            <ConferenceInfo
              visible={
                !(_userIdleMode && _userDevice?.options?.hideLogos === 1)
              }
              showLabel={
                _userIdleMode &&
                _userDevice?.options?.hideUserInterfaceElements === 1
              }
            />
            <Notice />
            <div id="videospace" onTouchStart={this._onVidespaceTouchStart}>
              {_sensoringDebugMode ? (
                <SelfLargeVideo visible={true} />
              ) : (
                <LargeVideo
                  visible={
                    !(
                      _userIdleMode && _userDevice?.options?.hideOtherText === 1
                    )
                  }
                />
              )}
              {_whiteboardReady && (
                <Whiteboard visible={!_shouldDisplayTileView} />
              )}
              <Message
                message={
                  _participantLength === 0
                    ? Labels.noParticipantMessage
                    : _videoMuted
                    ? Labels.videoMutedMessage
                    : Labels.noOneOnStage
                }
                visible={
                  !_isWhiteboardOpen &&
                  !_shouldDisplayTileView &&
                  (_participantLength === 0 ||
                    _videoMuted ||
                    _largeVideoParticipant == null) &&
                  !(_userIdleMode && _userDevice?.options?.hideOtherText === 1)
                }
              />
              {_oddRotation && _userDevice?.showDigitalSignage && (
                <div
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%'
                  }}>
                  <VWDigitalSignage
                    options={{
                      ..._userDevice?.digitalSignage,
                      signageStyle: 'replace'
                    }}
                    showLeave={false}
                    url={_userDevice?.digitalSignage?.oddPairingUrl}
                  />
                </div>
              )}
              <div id="conference" style={{ display: 'flex' }}>
                <ToolboxWeb
                  visible={
                    !(_userIdleMode && _userDevice?.options?.hideButtons === 1)
                  }
                />
                <JoinAudioVideo idleMode={_userIdleMode} />
                <StageFilmstrip
                  isVisible={!_isWhiteboardOpen && !_videoMuted}
                />
                <RemoteFilmstrip
                  visible={
                    !(
                      _userIdleMode && _userDevice?.options?.hideVideoTray === 1
                    )
                  }
                />
                <Toolbox
                  visible={
                    !(_userIdleMode && _userDevice?.options?.hideButtons === 1)
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {splits.length >= 2 && _isBrowser ? (
          <div
            style={{
              display: 'flex',
              flex: splits[1],
              width: '100%',
              height: '100%'
            }}>
            <VWBrowser options={_isBrowserOptions} />
          </div>
        ) : splits.length >= 2 &&
          _isDigitalSignage &&
          !_activeParticipants.length &&
          !_oddRotation ? (
          <div
            style={{
              display: 'flex',
              flex: splits[1],
              width: '100%',
              height: '100%'
            }}>
            <VWDigitalSignage options={_isDigitalSignageOptions} />
          </div>
        ) : null}
        {_conferenceSetting && (
          <Drawer
            PaperProps={{
              sx: { background: 'rgba(0,0,0,0.8)' }
            }}
            anchor={_clientWidth < 500 ? 'bottom' : 'left'}
            onClose={() => this.toggleSettings(false)}
            open={_conferenceSetting}>
            <Box
              onClick={() => this._startTimer()}
              role="presentation"
              sx={{
                width: _clientWidth < 500 ? 'auto' : 398,
                height: _clientHeight
              }}>
              <List style={{ padding: '10px', height: '100%' }}>
                <ConferenceSetting
                  open={_conferenceSetting}
                  onClose={() => this.toggleSettings(false)}
                />
              </List>
            </Box>
          </Drawer>
        )}
        {_conferenceGroups && (
          <Drawer
            PaperProps={{
              sx: { background: 'rgba(0,0,0,0.8)', width: '100%' }
            }}
            anchor={_clientWidth < 500 ? 'bottom' : 'left'}
            onClose={() => this.toggleGroups(false)}
            open={_conferenceGroups}>
            <Box
              role="presentation"
              sx={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                top: 0,
                zIndex: 999,
                width: '100%',
                overflow: 'scroll'
              }}>
              <Home
                onClose={() => this.toggleGroups(false)}
                onLeave={() => {
                  this.toggleGroups(false);
                  this.props.dispatch(leaveConference('/groups'));
                }}
                snapshot={true}
              />
            </Box>
          </Drawer>
        )}
      </div>
    );
  }

  toggleSettings(open) {
    this.props.dispatch(toggleSettings(open));
    if (open) {
      this._startTimer();
    } else {
      this._clearTimer();
    }
  }

  toggleGroups(open) {
    this.props.dispatch(toggleGroups(open));
  }

  renderWhiteLayer() {
    if (this.props._userIdleMode) {
      return <div className="whiteLayer" />;
    }

    return null;
  }

  /**
   * Sets custom background opacity based on config. It also applies the
   * opacity on parent element, as the parent element is not accessible directly,
   * only though it's child.
   *
   * @param {Object} element - The DOM element for which to apply opacity.
   *
   * @private
   * @returns {void}
   */
  _setBackground(element) {
    if (!element) {
      return;
    }

    // if (this.props._backgroundAlpha !== undefined) {
    // const elemColor = element.style.background;
    // const alphaElemColor = setColorAlpha(elemColor, this.props._backgroundAlpha);

    element.style.background = GradientBackground;
    if (element.parentElement) {
      // const parentColor = element.parentElement.style.background;
      // const alphaParentColor = setColorAlpha(parentColor, this.props._backgroundAlpha);
      element.parentElement.style.background = GradientBackground;
    }

    // }
  }

  /**
   * Handler used for touch start on Video container.
   *
   * @private
   * @returns {void}
   */
  _onVidespaceTouchStart() {
    // this.props.dispatch(toggleToolboxVisible());
  }

  /**
   * Updates the Redux state when full screen mode has been enabled or
   * disabled.
   *
   * @private
   * @returns {void}
   */
  _onFullScreenChange() {
    this.props.dispatch(fullScreenChanged(APP.UI.isFullScreen()));
  }

  /**
   * Triggers iframe API mouseEnter event.
   *
   * @param {MouseEvent} event - The mouse event.
   * @private
   * @returns {void}
   */
  // eslint-disable-next-line no-unused-vars
  _onMouseEnter(event) {
    APP.API.notifyMouseEnter(event);
  }

  /**
   * Triggers iframe API mouseLeave event.
   *
   * @param {MouseEvent} event - The mouse event.
   * @private
   * @returns {void}
   */
  // eslint-disable-next-line no-unused-vars
  _onMouseLeave(event) {
    APP.API.notifyMouseLeave(event);
  }

  /**
   * Triggers iframe API mouseMove event.
   *
   * @param {MouseEvent} event - The mouse event.
   * @private
   * @returns {void}
   */
  // eslint-disable-next-line no-unused-vars
  _onMouseMove(event) {
    APP.API.notifyMouseMove(event);
  }

  /**
   * Displays the toolbar.
   *
   * @private
   * @returns {void}
   */
  _onShowToolbar() {
    this.props.dispatch(showToolbox());
  }

  /**
   * Until we don't rewrite UI using react components
   * we use UI.start from old app. Also method translates
   * component right after it has been mounted.
   *
   * @inheritdoc
   */
  _start() {
    APP.UI.start();
    APP.UI.registerListeners();
    APP.UI.bindEvents();
    FULL_SCREEN_EVENTS.forEach((name) =>
      document.addEventListener(name, this._onFullScreenChange)
    );
    const { dispatch } = this.props;
    dispatch(init());
    // maybeShowSuboptimalExperienceNotification(dispatch, t);
    this.startIdleTimer();
    this.toggleGroups(false);
  }

  startIdleTimer() {
    clearTimeout(this.idleInterval);
    this.props.dispatch(updateSleepSettings({ userIdleMode: false }));
    this.idleInterval = setTimeout(() => {
      // console.log('isUserIdle', true);
      this.props.dispatch(updateSleepSettings({ userIdleMode: true }));
    }, 10000);
  }
}

Conference.propTypes = {
  dispatch: PropTypes.func,
  t: PropTypes.any,
  _userDevice: PropTypes.object,
  _isWhiteboardOpen: PropTypes.bool,
  _whiteboardReady: PropTypes.bool,
  _userIdleMode: PropTypes.bool,
  _isBrowser: PropTypes.bool,
  _isBrowserOptions: PropTypes.object,
  _isDigitalSignage: PropTypes.bool,
  _isDigitalSignageOptions: PropTypes.object,
  _videoMuted: PropTypes.bool,
  _shouldDisplayTileView: PropTypes.bool,
  _layoutClassName: PropTypes.string,
  _largeVideoParticipant: PropTypes.object,
  _roomName: PropTypes.string,
  _room: PropTypes.string,
  _notificationsVisible: PropTypes.bool,
  _mouseMoveCallbackInterval: PropTypes.number,
  _participantLength: PropTypes.number,
  _isPortrait: PropTypes.bool,
  _clientWidth: PropTypes.number,
  _clientHeight: PropTypes.number,
  _sensoringDebugMode: PropTypes.bool,
  _oddRotation: PropTypes.bool,
  _activeParticipants: PropTypes.array,
  _conferenceSetting: PropTypes.bool,
  _conferenceGroups: PropTypes.bool
};

/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code Conference} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {Props}
 */
function _mapStateToProps(state) {
  const { mouseMoveCallbackInterval } = state['features/base/config'];
  const { clientWidth, clientHeight } = state['features/base/responsive-ui'];
  const { sensoringDebugMode } = state['features/large-video'];
  const {
    userIdleMode,
    userBrowserMode,
    browserOptions,
    userDigitalSignageMode,
    digitalSignageOptions,
    conferenceSetting,
    conferenceGroups
  } = state['features/settings'];
  const tracks = state['features/base/tracks'];
  const tileViewActive = shouldDisplayTileView(state);

  return {
    _shouldDisplayTileView: tileViewActive,
    _userDevice: state['features/login'].user?.device,
    _room: state['features/base/conference'].room,
    _participantLength: getRemoteParticipantCount(state),
    _isWhiteboardOpen: isWhiteboardVisible(state),
    _whiteboardReady:
      isWhiteboardEnabled(state) && Boolean(getCollabDetails(state)),
    _largeVideoParticipant: getLargeVideoParticipant(state),
    _userIdleMode: userIdleMode,
    _isBrowser: userBrowserMode,
    _isBrowserOptions: browserOptions,
    _isDigitalSignage: userDigitalSignageMode,
    _isDigitalSignageOptions: digitalSignageOptions,
    _conferenceSetting: conferenceSetting,
    _conferenceGroups: conferenceGroups,
    _oddRotation: state['features/large-video'].oddRotation,
    _activeParticipants: state['features/filmstrip'].activeParticipants,
    _videoMuted: isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO),
    _layoutClassName:
      LAYOUT_CLASSNAMES[
        tileViewActive ? LAYOUTS.TILE_VIEW : LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW
      ],
    _mouseMoveCallbackInterval: mouseMoveCallbackInterval,
    _roomName: getConferenceNameForTitle(state),
    _notificationsVisible: shouldDisplayNotifications(state),
    _isPortrait: clientHeight > clientWidth,
    _clientWidth: clientWidth,
    _clientHeight: clientHeight,
    _sensoringDebugMode: sensoringDebugMode
  };
}

export default React.memo(
  withRouter(
    reactReduxConnect(_mapStateToProps)(withBoundary(translate(Conference)))
  )
);
